<template>
    <main class="picking-v2">
        <h1>Order picking</h1>

        <section class="picking-v2__panel picking-v2__search-panel">
            <label for="picking-reference">Zoek een picklijst</label>
            <form class="picking-v2__search" @submit.prevent="openPicking">
                <input
                    id="picking-reference"
                    ref="reference"
                    v-model.trim="reference"
                    autocomplete="off"
                    placeholder="Ordernummer, extern ordernummer of picklijstcode"
                    :disabled="claiming"
                >
                <button type="submit" class="picking-v2__primary" :disabled="claiming">
                    <i class="ak-icon" aria-hidden="true">search</i>
                    {{ claiming ? 'Openen…' : 'Open picklijst' }}
                </button>
            </form>
            <p v-if="message" class="picking-v2__message" role="alert">{{ message }}</p>
        </section>

        <section class="picking-v2__panel">
            <h2>Voltooide picklijsten</h2>
            <p v-if="loading" class="picking-v2__empty">Picklijsten laden…</p>
            <p v-else-if="!recent.length" class="picking-v2__empty">Je hebt nog geen picklijsten voltooid.</p>
            <div v-else class="picking-v2__recent">
                <article v-for="entry in recent" :key="entry.id" class="picking-v2__recent-row">
                    <div class="picking-v2__recent-details">
                        <strong>{{ entry.customerName }}</strong>
                        <span class="picking-v2__recent-reference">Bestelling {{ entry.orderCode }} <span class="picking-v2__recent-picking-code">· Picklijst {{ entry.code }}</span></span>
                        <i v-if="entry.hasPartialDelivery" class="ak-icon text--color-warning picking-v2__partial" title="Deellevering" aria-label="Deellevering">warning</i>
                    </div>
                    <div class="picking-v2__actions">
                        <button type="button" @click="print(entry, 'picking-list')"><i class="ak-icon">print</i> Picklijst</button>
                        <button type="button" @click="print(entry, 'shipping-list')"><i class="ak-icon">print</i> Leverbon</button>
                        <button v-if="entry.hasShippingLabels" type="button" @click="print(entry, 'shipping-label')"><i class="ak-icon">print</i> Verzendlabel</button>
                        <router-link :to="entry.detailPath"><i class="ak-icon">zoom_in</i> Bekijk order</router-link>
                    </div>
                </article>
            </div>
        </section>

        <p v-if="printError" class="picking-v2__toast" role="alert">{{ printError }}</p>

        <picking-v2-form
            v-if="picking && !isCompleted"
            :picking="picking"
            :saving="saving"
            :fields="fieldErrors"
            :error="modalError"
            @close="closePicking"
            @open-order="openOrder"
            @save="completePicking"
        />
        <picking-v2-completed
            v-if="picking && isCompleted"
            :picking="picking"
            :printing="printing"
            :error="printError"
            @close="newPicking"
            @new="newPicking"
            @print="print(picking, $event)"
        />
    </main>
</template>

<script>
import PickingV2Form from '../picking/PickingV2Form.vue';
import PickingV2Completed from '../picking/PickingV2Completed.vue';
import {printJobs} from '../../custom-actions';

export default {
    name: 'PickingV2',
    components: {PickingV2Form, PickingV2Completed},
    data() {
        return {
            reference: '',
            recent: [],
            picking: null,
            message: '',
            modalError: '',
            printError: '',
            fieldErrors: {},
            loading: false,
            claiming: false,
            saving: false,
            printing: '',
            heartbeatTimer: null,
            navigatingToOrder: false,
        };
    },
    computed: {
        isCompleted() {
            return this.$route.name === 'picking-v2-completed';
        },
    },
    watch: {
        '$route.fullPath': 'loadRoute',
    },
    async created() {
        await this.loadDashboard();
        await this.loadRoute();
    },
    beforeDestroy() {
        this.stopHeartbeat();
        if (this.picking && !this.isCompleted && !this.navigatingToOrder && navigator.sendBeacon) {
            navigator.sendBeacon(this.$generateUrl(`/picking-v2/${this.picking.id}/release`), '{}');
        }
    },
    methods: {
        async request(path, options = {}) {
            const csrfToken = this.$getSetting('security.csrfToken');
            const response = await fetch(this.$generateUrl(path), {
                credentials: 'same-origin',
                ...options,
                headers: {
                    'Content-Type': 'application/json',
                    ...(csrfToken ? {'X-CSRF-Token': csrfToken} : {}),
                    ...(options.headers || {}),
                },
            });
            const payload = await response.json();
            if (!response.ok || !payload.success) {
                throw payload.error || {message: 'Er ging iets mis. Probeer opnieuw.'};
            }
            return payload.data;
        },
        async loadDashboard() {
            this.loading = true;
            try {
                const data = await this.request('/picking-v2');
                this.recent = data.recent;
            } catch (error) {
                this.message = error.message;
            } finally {
                this.loading = false;
                this.focusSearch();
            }
        },
        async loadRoute() {
            this.stopHeartbeat();
            if (!this.$route.params.pickingId) {
                this.picking = null;
                const reference = this.$route.query.pickingCode || this.$route.query.orderCode;
                if (reference) {
                    this.reference = reference;
                    await this.openPicking();
                    return;
                }
                this.focusSearch();
                return;
            }
            try {
                this.picking = await this.request(`/picking-v2/${this.$route.params.pickingId}`);
                if (!this.isCompleted) {
                    this.startHeartbeat();
                }
            } catch (error) {
                this.message = error.message;
                await this.$router.replace({name: 'picking-v2'});
            }
        },
        async openPicking() {
            this.claiming = true;
            this.message = '';
            try {
                const data = await this.request('/picking-v2/claim', {
                    method: 'POST',
                    body: JSON.stringify({reference: this.reference}),
                });
                this.picking = data;
                await this.$router.push({name: 'picking-v2-active', params: {pickingId: data.id}});
            } catch (error) {
                this.message = error.message;
                this.focusSearch();
            } finally {
                this.claiming = false;
            }
        },
        async completePicking(values) {
            this.saving = true;
            this.modalError = '';
            this.fieldErrors = {};
            try {
                this.picking = await this.request(`/picking-v2/${this.picking.id}/complete`, {
                    method: 'POST',
                    body: JSON.stringify(values),
                });
                this.stopHeartbeat();
                await this.loadDashboard();
                await this.$router.replace({name: 'picking-v2-completed', params: {pickingId: this.picking.id}});
            } catch (error) {
                this.modalError = error.message;
                this.fieldErrors = error.fields || {};
            } finally {
                this.saving = false;
            }
        },
        async closePicking() {
            try {
                await this.request(`/picking-v2/${this.picking.id}/release`, {method: 'POST'});
            } catch (error) {
                this.message = error.message;
            }
            await this.newPicking();
        },
        async openOrder() {
            this.navigatingToOrder = true;
            await this.$router.push(this.picking.detailPath);
        },
        async newPicking() {
            this.stopHeartbeat();
            this.picking = null;
            this.reference = '';
            this.modalError = '';
            this.fieldErrors = {};
            if (this.$route.name !== 'picking-v2') {
                await this.$router.replace({name: 'picking-v2'});
            }
            this.focusSearch();
        },
        async print(entry, document) {
            this.printError = '';
            this.printing = document;
            try {
                const jobs = await this.request(`/picking-v2/${entry.id}/print/${document}`, {method: 'POST'});
                if (!jobs.length) {
                    throw {message: 'Voor deze picking zijn geen labels om af te drukken.'};
                }
                printJobs(jobs);
            } catch (error) {
                this.printError = error.message;
            } finally {
                this.printing = '';
            }
        },
        startHeartbeat() {
            this.heartbeatTimer = window.setInterval(() => {
                this.request(`/picking-v2/${this.picking.id}/heartbeat`, {method: 'POST'}).catch(() => {});
            }, 60000);
        },
        stopHeartbeat() {
            window.clearInterval(this.heartbeatTimer);
            this.heartbeatTimer = null;
        },
        focusSearch() {
            this.$nextTick(() => this.$refs.reference && this.$refs.reference.focus());
        },
    },
};
</script>

<style lang="scss">
.picking-v2 {
    --picking-root: #fff;
    --picking-surface: #fff;
    --picking-subtle: #f9fafb;
    --picking-border: #e5e7eb;
    box-sizing: border-box;
    width: 100%;
    max-width: 1200px;
    min-height: calc(100% - 60px);
    margin: 30px auto;
    padding: 27px 30px;
    color: var(--color-text, #111827);
    background: var(--picking-root);
    border: 1px solid var(--picking-border);
    border-radius: 10px;
    box-shadow: 0 4px 6px rgba(0, 0, 0, .05);
}
.ak-dark .picking-v2 {--picking-root: #12181f; --picking-surface: #18232d; --picking-subtle: #18232d; --picking-border: rgba(255, 255, 255, .2);}
.picking-v2 h1 {margin: 0 0 20px; font-size: 32px; line-height: 1.25;}
.picking-v2 h2 {margin: 0; font-size: 16px; font-weight: 400;}
.picking-v2__panel {padding: 24px; border: 1px solid var(--picking-border); border-radius: 10px; background: var(--picking-surface);}
.picking-v2__search-panel {margin-bottom: 16px; background: var(--picking-subtle);}
.picking-v2__search-panel label {display: block; margin-bottom: 16px; font-size: 16px;}
.picking-v2__search {display: flex; height: 59px;}
.picking-v2__search input {min-width: 0; flex: 1; padding: 0 16px; color: var(--color-text); background: var(--input-background, #fff); border: 1px solid var(--picking-border); border-radius: 8px 0 0 8px; font: inherit;}
.picking-v2__primary {display: inline-flex; align-items: center; justify-content: center; gap: 8px; min-width: 117px; padding: 0 24px; color: #fff; background: #298afb; border: 0; border-radius: 0 8px 8px 0; cursor: pointer;}
.picking-v2__primary:disabled {opacity: .6; cursor: wait;}
.picking-v2__message, .picking-v2__toast {margin: 12px 0 0; color: var(--color-danger, #d92d20);}
.picking-v2__recent {margin-top: 16px; border: 1px solid var(--picking-border); border-radius: 8px; overflow: hidden;}
.picking-v2__recent-row {display: flex; align-items: center; justify-content: space-between; min-height: 64px; padding: 0 8px; border-bottom: 1px solid var(--picking-border);}
.picking-v2__recent-row:last-child {border-bottom: 0;}
.picking-v2__recent-details {display: flex; flex: 1 1 auto; align-items: center; gap: 16px; min-width: 0; padding: 12px 16px;}
.picking-v2__recent-details strong {min-width: 0; font-size: 16px; font-weight: 600; overflow: hidden; text-overflow: ellipsis; white-space: nowrap;}
.picking-v2__recent-reference {font-size: 13px; white-space: nowrap;}
.picking-v2__recent-picking-code {color: rgba(var(--color-text-rgb), .6);}
.picking-v2__partial {font-size: 22px;}
.picking-v2__actions {display: flex; align-items: center; justify-content: flex-end; gap: 0;}
.picking-v2__actions button, .picking-v2__actions a {display: inline-flex; align-items: center; gap: 8px; min-height: 44px; padding: 0 18px; color: #298afb; background: transparent; border: 0; border-right: 1px solid var(--picking-border); font: inherit; font-size: 14px; text-decoration: none; cursor: pointer;}
.picking-v2__actions > :last-child {border-right: 0;}
.picking-v2__actions .ak-icon {font-size: 20px;}
.picking-v2__empty {margin: 20px 0 0; color: rgba(var(--color-text-rgb), .6);}
.picking-v2__toast {position: fixed; right: 24px; bottom: 24px; z-index: 1200; max-width: 420px; padding: 16px; color: #fff; background: #b42318; border-radius: 8px; box-shadow: 0 10px 24px rgba(0, 0, 0, .2);}
@media (max-width: 900px) {
    .picking-v2 {padding: 20px;}
    .picking-v2__recent-row {align-items: flex-start; flex-direction: column; padding: 12px;}
    .picking-v2__recent-details {align-items: flex-start; flex-direction: column; gap: 6px; padding: 0;}
    .picking-v2__actions {width: 100%; flex-wrap: wrap; justify-content: flex-start;}
    .picking-v2__actions button, .picking-v2__actions a {padding: 0 10px; border-right: 0;}
}
@media (max-width: 560px) {
    .picking-v2 {padding: 16px; border-radius: 0;}
    .picking-v2__search {height: auto; flex-direction: column;}
    .picking-v2__search input, .picking-v2__primary {width: 100%; min-height: 52px; border-radius: 8px;}
    .picking-v2__primary {margin-top: 8px;}
}
</style>
